import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "./ui/button";
import { LayoutDashboard, LogOut, PlusCircle, Menu, X, Search } from "lucide-react";
import { Avatar, AvatarFallback } from "./ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel
} from "./ui/dropdown-menu";
import ChileIndicators from "./ChileIndicators";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const panelPath = user?.role === "super_admin" ? "/admin" : user?.role === "seller" ? "/seller" : "/investor";

  // Close mobile menu on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname, location.hash]);

  const linkCls = (path) =>
    `text-sm font-medium transition-colors ${
      location.pathname === path ? "text-slate-900" : "text-slate-500 hover:text-slate-900"
    }`;

  const mobileLinkCls = "block w-full px-4 py-3 text-base font-medium text-slate-700 hover:bg-slate-50 active:bg-slate-100 transition";

  return (
    <header className="glass-header sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between gap-2">
        <Link to="/" className="flex items-center shrink-0" data-testid="nav-logo-link">
          <img src="/logo-brand.png" alt="DerechoLlave" className="h-10 sm:h-14 w-auto" />
        </Link>

        <nav className="hidden md:flex items-center gap-5">
          <Link
            to="/marketplace"
            className={`group inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-semibold border transition ${
              location.pathname === "/marketplace"
                ? "bg-slate-900 text-amber-400 border-slate-900"
                : "bg-amber-400 text-slate-900 border-amber-400 hover:bg-amber-300 hover:border-amber-300"
            }`}
            data-testid="nav-marketplace-link"
          >
            <Search className="w-3.5 h-3.5" />
            <span>Ver catálogo de negocios</span>
          </Link>
          <Link to="/#como-funciona" className={linkCls("/como-funciona")} data-testid="nav-how-link">Cómo funciona</Link>
          <Link to="/publicar" className={linkCls("/publicar")} data-testid="nav-sell-link">Publicar negocio</Link>
          <Link to="/contacto" className={linkCls("/contacto")} data-testid="nav-contact-link">Contacto</Link>
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <div className="hidden sm:block"><ChileIndicators variant="light" /></div>
          {!user ? (
            <>
              <Button variant="ghost" onClick={() => navigate("/login")} data-testid="nav-login-btn" className="hidden sm:inline-flex">
                Ingresar
              </Button>
              <Button onClick={() => navigate("/register")} className="btn-primary hidden sm:inline-flex" data-testid="nav-register-btn">
                Crear cuenta
              </Button>
            </>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 px-2 py-1 rounded-full hover:bg-slate-100 transition" data-testid="nav-user-menu">
                  <Avatar className="w-8 h-8">
                    <AvatarFallback className="bg-slate-900 text-white text-xs">
                      {user.full_name?.slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:inline text-sm font-medium">{user.full_name?.split(" ")[0]}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">{user.full_name}</span>
                    <span className="text-xs text-slate-500">{user.email}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate(panelPath)} data-testid="nav-panel-link">
                  <LayoutDashboard className="w-4 h-4 mr-2" /> Mi panel
                </DropdownMenuItem>
                {user.role === "seller" && (
                  <DropdownMenuItem onClick={() => navigate("/seller/new")} data-testid="nav-new-business">
                    <PlusCircle className="w-4 h-4 mr-2" /> Publicar negocio
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} data-testid="nav-logout-btn">
                  <LogOut className="w-4 h-4 mr-2" /> Cerrar sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Mobile menu toggle */}
          <button
            type="button"
            aria-label={mobileOpen ? "Cerrar menú" : "Abrir menú"}
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden w-10 h-10 inline-flex items-center justify-center rounded-md hover:bg-slate-100 transition"
            data-testid="nav-mobile-toggle"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu drawer */}
      {mobileOpen && (
        <div className="md:hidden border-t border-zinc-200 bg-white shadow-sm" data-testid="nav-mobile-drawer">
          <nav className="py-2">
            <Link
              to="/marketplace"
              className="flex items-center justify-center gap-2 mx-3 my-2 px-4 py-3 rounded-lg bg-amber-400 text-slate-900 font-bold text-base shadow-sm active:bg-amber-300 transition"
              data-testid="nav-mobile-marketplace"
            >
              <Search className="w-4 h-4" /> Ver catálogo de negocios
            </Link>
            <Link to="/#como-funciona" className={mobileLinkCls} data-testid="nav-mobile-how">Cómo funciona</Link>
            <Link to="/publicar" className={mobileLinkCls} data-testid="nav-mobile-sell">Publicar negocio</Link>
            <Link to="/contacto" className={mobileLinkCls} data-testid="nav-mobile-contact">Contacto</Link>
            <div className="border-t border-zinc-100 my-2" />
            <div className="px-4 py-2"><ChileIndicators variant="light" /></div>
            {!user ? (
              <div className="border-t border-zinc-100 mt-2 p-3 grid grid-cols-2 gap-2">
                <Button variant="outline" onClick={() => { setMobileOpen(false); navigate("/login"); }} data-testid="nav-mobile-login">Ingresar</Button>
                <Button className="btn-primary" onClick={() => { setMobileOpen(false); navigate("/register"); }} data-testid="nav-mobile-register">Crear cuenta</Button>
              </div>
            ) : null}
          </nav>
        </div>
      )}
    </header>
  );
}
